import type { FC } from 'react';
import { useId } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { Box, Heading, IconLoader, Stack, Text, TileBar, VisuallyHidden } from '@eneco/sparky';
import { ArrowRightIcon, ChevronRightIcon } from '@eneco/sparky/icons';
import type { LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';

import type { QuickLinksContent } from './quick-links-content.types';

/**
 * `QuickLinks` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/QuickLinks/QuickLinks.tsx`): a
 * `TileBar` of link rows, each with an icon, label and trailing chevron; the title is either shown or
 * kept visually hidden (still announced to assistive tech) depending on `showTitle`.
 *
 * See `quick-links-content.types.ts` — the generated Sanity schema's `items` has no resolvable content
 * yet, so this only ever renders the title on `?_cms=sanity` today; `items` is genuinely populated on
 * `?_cms=sitecore`, where each `icon` needs its droplist `.name` read out.
 */
const QuickLinks: FC = () => {
  const { adapter } = useCms();
  const content = useContent<QuickLinksContent>();
  const id = useId();

  const items = (content.items ?? []).flatMap((item, index) => {
    const link = adapter.toLink(item.link);
    const icon = typeof item.icon === 'string' ? item.icon : item.icon?.name;
    return link?.href && link.text ? [{ key: item._key ?? String(index), link, icon }] : [];
  });

  const titleElement = content.title ? (
    <Heading size="2XS" as="h3">
      {content.title}
    </Heading>
  ) : null;

  return (
    <nav aria-labelledby={id}>
      {content.showTitle ? (
        <Box paddingBottom="4" id={id}>
          {titleElement}
        </Box>
      ) : (
        <VisuallyHidden id={id}>{titleElement}</VisuallyHidden>
      )}
      <TileBar direction="column">
        {items.map(({ key, link, icon }) => {
          const linkValue: LinkFieldValue = { href: link.href, linktype: link.linkType ?? 'internal', target: link.target ?? '', querystring: '', anchor: '' };
          return (
            <TileBar.Item key={key} linkType={linkValue.linktype} linkValue={linkValue}>
              <Stack direction="row" gap="4" alignY="center">
                <IconLoader iconName={icon} fallbackIcon={<ArrowRightIcon />} />
                <Stack.Item grow>
                  <Text align="left" weight="bold" size="BodyS">
                    {link.text}
                  </Text>
                </Stack.Item>
                <ChevronRightIcon />
              </Stack>
            </TileBar.Item>
          );
        })}
      </TileBar>
    </nav>
  );
};

export default QuickLinks;
